import React, { useState } from 'react';
import {
	Button,
	Collapse,
	ColorPicker,
	Dropdown,
	Flex,
	Input,
	InputNumber,
	Modal,
	Popover,
	Select,
	Space,
	Typography,
	Tooltip,
} from 'antd';
import {
	CopyOutlined,
	DeleteOutlined,
	PlusOutlined,
	EditOutlined,
	EllipsisOutlined,
	UndoOutlined,
	EyeOutlined,
	WarningOutlined,
} from '@ant-design/icons';
import DesignModeControl from 'admin/components/common/DesignModeControl';
import DesignTokenPicker, { TokenPreview } from 'admin/components/common/DesignTokenPicker';
import {
	TEXT_PRESET_PROPERTIES,
	TEXT_PRESET_ENUM_VALUES,
	createDesignTokenId,
	getCompatibleDesignTokens,
	getTextPresetInheritedAssignment,
	resolveDesignModeValue,
	getTextPresetTokenId,
	type DesignDefinition,
	type TextPreset,
	type TextPresetProperty,
	type TextPresetPropertyValue,
	type TextPresetValue,
} from 'common/design-tokens';
import { getPlatformReferenceWidth, orderMediaQueryConfig } from 'common/utils/story-media-query';
import { generateId } from 'utils/generate-id';
import t from 'utils/translate';
import type { StoryDefaultPlatform, StoryMediaQuery } from 'types/story';
import TextPresetPreview, { type TextPresetPreviewFont } from './TextPresetPreview';
import { getTextPresetDisplayValue, getTextPresetPreviewStyle } from './text-preset-display';
import TextPresetPlatformLabel from './TextPresetPlatformLabel';
import css from './TextPresetsEditor.scss';

// Standard Brand Kit preview only; stored Base stays portable across Stories.
const PREVIEW_MEDIA_QUERY = {
	defaultPlatform: 'desktop',
	config: {
		desktop: { uiName: 'Desktop', minWidth: null, maxWidth: null },
		tablet: { uiName: 'Tablet', minWidth: null, maxWidth: 1024 },
		mobile: { uiName: 'Mobile', minWidth: null, maxWidth: 767 },
	},
} as StoryMediaQuery;
const defaultLiteral = (property: TextPresetProperty, design: DesignDefinition): TextPresetValue => {
	if (property === 'fontFamily') return design.defaults.root.fontFamily.base;
	if (property === 'fontWeight') return 400;
	if (property === 'lineHeight') return 1.5;
	if (property === 'color' || property === 'backgroundColor') return 'rgba(0, 0, 0, 1)';
	if (property in TEXT_PRESET_ENUM_VALUES) return 'none';
	return property === 'fontSize' ? '18px' : '0px';
};
function LiteralControl({
	property,
	value,
	fonts,
	disabled,
	onChange,
}: {
	property: TextPresetProperty;
	value: TextPresetValue;
	fonts: { fontFamily: string }[];
	disabled?: boolean;
	onChange: (value: TextPresetValue) => void;
}) {
	if (property === 'fontFamily') {
		const families = [...new Set(fonts.map(font => font.fontFamily))];
		return (
			<Select
				disabled={disabled}
				className={css.select}
				popupMatchSelectWidth={false}
				aria-label={property}
				showSearch
				optionFilterProp="label"
				value={String(value)}
				options={[
					...families.map(family => ({ value: family, label: family })),
					...(!families.includes(String(value))
						? [{ value: String(value), label: `${value} (${t('textPresets.unavailable')})` }]
						: []),
				]}
				onChange={onChange}
			/>
		);
	}
	if (property === 'color' || property === 'backgroundColor')
		return (
			<ColorPicker
				disabled={disabled}
				value={String(value)}
				showText
				onChange={color => onChange(color.toRgbString())}
			/>
		);
	if (property in TEXT_PRESET_ENUM_VALUES)
		return (
			<Select
				disabled={disabled}
				className={css.select}
				aria-label={property}
				value={String(value)}
				options={TEXT_PRESET_ENUM_VALUES[property as keyof typeof TEXT_PRESET_ENUM_VALUES].map(item => ({
					value: item,
					label: item,
				}))}
				onChange={onChange}
			/>
		);
	const number = typeof value === 'number' ? value : Number.parseFloat(value);
	return (
		<Flex wrap gap="small" className={css.numberControl}>
			<InputNumber
				disabled={disabled}
				suffix={property !== 'fontWeight' && property !== 'lineHeight' ? 'px' : undefined}
				aria-label={property}
				value={Number.isFinite(number) ? number : null}
				onChange={next => {
					if (next !== null)
						onChange(
							property === 'fontWeight' || (property === 'lineHeight' && typeof value === 'number')
								? next
								: `${next}px`
						);
				}}
			/>
			{property === 'lineHeight' ? (
				<Select
					disabled={disabled}
					className={css.unit}
					aria-label={t('textPresets.unit')}
					value={typeof value === 'number' ? 'number' : 'px'}
					options={[
						{ value: 'number', label: t('textPresets.unitless') },
						{ value: 'px', label: 'px' },
					]}
					onChange={unit => onChange(unit === 'number' ? number || 0 : `${number || 0}px`)}
				/>
			) : null}
		</Flex>
	);
}
function AssignmentCell({
	property,
	display,
	assigned,
	inherited,
	design,
	fonts,
	modeSelection,
	disabled,
	isBase,
	onChange,
}: {
	property: TextPresetProperty;
	display: ReturnType<typeof getTextPresetDisplayValue>;
	assigned: TextPresetValue | undefined;
	inherited?: { value: TextPresetValue; source: string };
	design: DesignDefinition;
	fonts: { fontFamily: string }[];
	modeSelection: Record<string, string>;
	disabled?: boolean;
	isBase: boolean;
	onChange: (value: TextPresetValue | undefined) => void;
}) {
	const [open, setOpen] = useState(false);
	const [editing, setEditing] = useState(false);
	const [search, setSearch] = useState('');
	const compatible = getCompatibleDesignTokens(design.tokens, property);
	const effective = assigned ?? inherited?.value;
	const tokenId = getTextPresetTokenId(effective);
	const token = tokenId ? compatible.find(item => item.id === tokenId) : undefined;
	const tokenValue = token ? resolveDesignModeValue<string | number>(token.value, modeSelection) : undefined;
	const inheritedHint =
		assigned === undefined && inherited ? `${t('textPresets.inherited')} ${inherited.source}` : undefined;
	const exactValue = display?.value ?? tokenValue ?? effective;
	const displayedValue =
		typeof exactValue === 'string' && /^[-+]?(?:\d+(?:\.\d+)?|\.\d+)px$/i.test(exactValue)
			? `${Number(parseFloat(exactValue).toFixed(1))}px`
			: exactValue;
	const label = token ? `${displayedValue} · ${token.name}` : tokenId ?? String(displayedValue ?? '—');
	const isColor = property === 'color' || property === 'backgroundColor';
	const openPicker = () => {
		setSearch('');
		setEditing(false);
		setOpen(true);
	};
	const hint = (
		<>
			{token ? <TokenPreview token={token} value={exactValue} /> : tokenId ?? String(exactValue ?? '—')}
			{display && display.cssValue !== display.authoredValue ? (
				<div>{`${display.authoredValue} → ${display.cssValue} → ${display.value}`}</div>
			) : null}
			{inheritedHint ? <div>{inheritedHint}</div> : null}
			{tokenId && !token ? <div>{t('textPresets.unresolved')}</div> : null}
		</>
	);
	return (
		<div className={css.assignment} data-inherited={assigned === undefined} data-open={open || editing}>
			{isColor && !tokenId && !open ? (
				<Tooltip title={inheritedHint}>
					<div className={css.colorTrigger}>
						<ColorPicker
							size="small"
							className={css.colorValue}
							open={editing && !disabled}
							value={String(effective ?? defaultLiteral(property, design))}
							disabled={disabled}
							showText={() => <span>{String(effective ?? '—')}</span>}
							onOpenChange={setEditing}
							onChangeComplete={color => onChange(color.toRgbString())}
						/>
					</div>
				</Tooltip>
			) : (
				<Popover
					trigger="click"
					placement="bottomLeft"
					open={(open || editing) && !disabled}
					onOpenChange={next => {
						if (!next) {
							setOpen(false);
							setEditing(false);
						}
					}}
					styles={{ container: { padding: 0 } }}
					content={
						open ? (
							<DesignTokenPicker
								search={search}
								onSearchChange={setSearch}
								onClose={() => setOpen(false)}
								modeSelection={modeSelection}
								sources={[
									{
										id: 'design',
										tokens: compatible,
										selectedTokenId: tokenId,
										onSelect: reference => {
											onChange(reference);
											setOpen(false);
										},
									},
								]}
								footer={
									tokenId ? (
										<Button
											type="text"
											onClick={() => {
												onChange(tokenValue ?? defaultLiteral(property, design));
												setOpen(false);
												setEditing(true);
											}}
										>
											{t('designTokens.unlink')}
										</Button>
									) : undefined
								}
							/>
						) : (
							<div
								className={css.literalEditor}
								onKeyDown={event => {
									if (event.key === 'Escape') {
										event.stopPropagation();
										setEditing(false);
									}
								}}
							>
								<LiteralControl
									property={property}
									value={effective ?? defaultLiteral(property, design)}
									fonts={fonts}
									disabled={disabled}
									onChange={onChange}
								/>
							</div>
						)
					}
				>
					<Tooltip title={hint} open={open || editing ? false : undefined}>
						<button
							type="button"
							className={tokenId ? css.tokenButton : css.literalButton}
							disabled={disabled}
							aria-label={`${t(`textPresets.properties.${property}`)}: ${label}${inheritedHint ? ` (${inheritedHint})` : ''}`}
							data-unresolved={!!tokenId && !token}
							onClick={() => (tokenId ? openPicker() : setEditing(true))}
						>
							{token?.type === 'color' ? (
								<span className={css.colorSwatch} style={{ backgroundColor: String(tokenValue) }} />
							) : null}
							{token ? (
								<span className={css.bindingLabel}>
									<span className={css.bindingValue}>{String(displayedValue)}</span>
									<span className={css.bindingName}>{token.name}</span>
								</span>
							) : (
								label
							)}
							{tokenId && !token ? <WarningOutlined /> : null}
						</button>
					</Tooltip>
				</Popover>
			)}
			{!tokenId ? (
				<Tooltip title={t('designTokens.connect')}>
					<button
						type="button"
						className={css.connectToken}
						disabled={disabled}
						aria-label={t('designTokens.connect')}
						onClick={openPicker}
					>
						<PlusOutlined />
					</button>
				</Tooltip>
			) : null}
			{assigned !== undefined ? (
				<Tooltip title={t(isBase ? 'textPresets.clearAssignment' : 'textPresets.revert')}>
					<button
						type="button"
						className={css.revert}
						disabled={disabled}
						aria-label={t(isBase ? 'textPresets.clearAssignment' : 'textPresets.revert')}
						onClick={() => {
							setOpen(false);
							setEditing(false);
							onChange(undefined);
						}}
					>
						<UndoOutlined />
					</button>
				</Tooltip>
			) : null}
		</div>
	);
}

export default function TextPresetsEditor({
	value,
	onChange,
	fonts,
	mediaQuery = PREVIEW_MEDIA_QUERY,
	disabled,
}: {
	value: DesignDefinition;
	onChange: (value: DesignDefinition) => void;
	fonts: TextPresetPreviewFont[];
	mediaQuery?: StoryMediaQuery;
	disabled?: boolean;
}) {
	const presets = value.textPresets ?? [];
	const [previewId, setPreviewId] = useState<string>();
	const previewPreset = presets.find(preset => preset.id === previewId);
	const [renaming, setRenaming] = useState<Pick<TextPreset, 'id' | 'name' | 'description'>>();
	const [activeKeys, setActiveKeys] = useState<string[]>([]);
	const [requestedModes, setRequestedModes] = useState<Record<string, string>>({});
	const modeSelection = Object.fromEntries(
		value.modeGroups.map(group => [
			group.id,
			group.modes.some(mode => mode.id === requestedModes[group.id])
				? requestedModes[group.id]
				: group.defaultModeId,
		])
	);
	const platforms = orderMediaQueryConfig(mediaQuery, { defaultFirst: true, minOrder: 'asc', maxOrder: 'desc' });
	for (const key of new Set(
		presets.flatMap(preset =>
			Object.values(preset.properties).flatMap(assignment => Object.keys(assignment.platforms ?? {}))
		)
	)) {
		if (!platforms.some(item => item.key === key))
			platforms.push({
				key: key as StoryDefaultPlatform,
				config: { uiName: `${key} (${t('textPresets.unsupportedPlatform')})`, minWidth: null, maxWidth: null },
			});
	}
	const columns = [
		{ key: 'base', label: 'Base' },
		...platforms.map(item => ({ key: item.key, label: item.config.uiName || item.key })),
	];
	const update = (id: string, change: (preset: TextPreset) => TextPreset) =>
		onChange({ ...value, textPresets: presets.map(preset => (preset.id === id ? change(preset) : preset)) });
	const setAssignment = (
		id: string,
		property: TextPresetProperty,
		platform: string,
		next: TextPresetValue | undefined
	) =>
		update(id, preset => {
			const properties = { ...preset.properties };
			const assignment: TextPresetPropertyValue = {
				...properties[property],
				platforms: { ...properties[property]?.platforms },
			};
			if (platform === 'base') {
				if (next === undefined) delete assignment.base;
				else assignment.base = next;
			} else if (next === undefined) delete assignment.platforms![platform as StoryDefaultPlatform];
			else assignment.platforms![platform as StoryDefaultPlatform] = next;
			if (!Object.keys(assignment.platforms ?? {}).length) delete assignment.platforms;
			if (assignment.base === undefined && !assignment.platforms) delete properties[property];
			else properties[property] = assignment;
			return { ...preset, properties };
		});
	const add = (source?: TextPreset) => {
		const preset: TextPreset = source
			? {
					...structuredClone(source),
					id: createDesignTokenId(
						`${source.id}-copy`,
						presets.map(item => item.id)
					),
					name: `${source.name} ${t('textPresets.copy')}`,
				}
			: {
					id: createDesignTokenId(
						`preset-${generateId(12)}`,
						presets.map(item => item.id),
						'preset'
					),
					name: t('textPresets.new'),
					properties: {},
				};
		onChange({ ...value, textPresets: [...presets, preset] });
		setActiveKeys([...activeKeys, preset.id]);
	};

	return (
		<Flex vertical gap="middle" className={css.root}>
			<Flex gap="middle" justify="space-between" align="center" wrap>
				<Typography.Text type="secondary" className={css.help}>
					{t('textPresets.help')}
				</Typography.Text>
				<Button
					type="text"
					className={css.addAction}
					icon={<PlusOutlined />}
					onClick={() => add()}
					disabled={disabled}
				>
					{t('textPresets.add')}
				</Button>
			</Flex>
			<Flex gap="small" wrap>
				{value.modeGroups.map(group => (
					<Space key={group.id}>
						<Typography.Text>{group.name}</Typography.Text>
						<DesignModeControl
							value={modeSelection[group.id]}
							modes={group.modes}
							disabled={disabled}
							onChange={id => setRequestedModes({ ...requestedModes, [group.id]: id })}
						/>
					</Space>
				))}
			</Flex>
			<Collapse
				activeKey={activeKeys}
				onChange={keys => setActiveKeys(typeof keys === 'string' ? [keys] : keys)}
				items={presets.map(preset => ({
					key: preset.id,
					label: (
						<span className={css.presetHeading}>
							{preset.name}
							<Tooltip title={t('textPresets.preview')}>
								<Button
									type="text"
									size="small"
									icon={<EyeOutlined />}
									aria-label={t('textPresets.preview')}
									onClick={event => {
										event.stopPropagation();
										setPreviewId(preset.id);
									}}
									onKeyDown={event => event.stopPropagation()}
								/>
							</Tooltip>
							<Dropdown
								trigger={['click']}
								disabled={disabled}
								menu={{
									items: [
										{ key: 'rename', label: t('textPresets.rename'), icon: <EditOutlined /> },
										{ key: 'duplicate', label: t('textPresets.duplicate'), icon: <CopyOutlined /> },
										{
											key: 'delete',
											label: t('designTokens.delete'),
											icon: <DeleteOutlined />,
											danger: true,
										},
									],
									onClick: ({ key, domEvent }) => {
										domEvent.stopPropagation();
										if (key === 'rename')
											setRenaming({
												id: preset.id,
												name: preset.name,
												description: preset.description,
											});
										if (key === 'duplicate') add(preset);
										if (key === 'delete')
											onChange({
												...value,
												textPresets: presets.filter(item => item.id !== preset.id),
											});
									},
								}}
							>
								<Button
									type="text"
									size="small"
									disabled={disabled}
									icon={<EllipsisOutlined />}
									aria-label={t('textPresets.actions')}
									onClick={event => event.stopPropagation()}
									onKeyDown={event => event.stopPropagation()}
								/>
							</Dropdown>
						</span>
					),
					children: (
						<Flex vertical gap="small">
							{preset.description ? (
								<Typography.Text type="secondary">{preset.description}</Typography.Text>
							) : null}
							<div
								className={css.tableScroll}
								role="region"
								aria-label={`${preset.name} ${t('textPresets.propertiesLabel')}`}
								// eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- keyboard scrolling
								tabIndex={0}
							>
								<table className={css.table} style={{ minWidth: 110 + columns.length * 145 }}>
									<thead>
										<tr>
											<th scope="col">{t('textPresets.propertiesLabel')}</th>
											{columns.map(column => (
												<th key={column.key} scope="col">
													<TextPresetPlatformLabel
														platform={column.key}
														label={column.label}
														referenceWidth={
															column.key === 'base'
																? undefined
																: getPlatformReferenceWidth(
																		mediaQuery,
																		column.key as StoryDefaultPlatform
																	)
														}
													/>
												</th>
											))}
										</tr>
									</thead>
									<tbody>
										{Object.entries(preset.properties).map(([key, assignment]) => {
											const property = key as TextPresetProperty;
											return (
												<tr key={property}>
													<th scope="row">
														<div className={css.propertyName}>
															{t(`textPresets.properties.${property}`)}
															<Button
																type="text"
																disabled={disabled}
																size="small"
																className={css.removeProperty}
																aria-label={`${t('designTokens.delete')} ${t(`textPresets.properties.${property}`)}`}
																icon={<DeleteOutlined />}
																onClick={() =>
																	update(preset.id, current => {
																		const properties = { ...current.properties };
																		delete properties[property];
																		return { ...current, properties };
																	})
																}
															/>
														</div>
													</th>
													{columns.map(column => {
														const inherited =
															column.key === 'base'
																? undefined
																: getTextPresetInheritedAssignment(
																		assignment,
																		column.key as StoryDefaultPlatform,
																		mediaQuery
																	);
														const source =
															columns.find(item => item.key === inherited?.source)
																?.label ?? inherited?.source;
														return (
															<td key={column.key}>
																<AssignmentCell
																	isBase={column.key === 'base'}
																	display={getTextPresetDisplayValue({
																		property,
																		assignment,
																		platform: column.key as
																			| 'base'
																			| StoryDefaultPlatform,
																		mediaQuery,
																		design: value,
																		modeSelection,
																	})}
																	property={property}
																	assigned={
																		column.key === 'base'
																			? assignment.base
																			: assignment.platforms?.[
																					column.key as StoryDefaultPlatform
																				]
																	}
																	inherited={
																		inherited
																			? { ...inherited, source: source! }
																			: undefined
																	}
																	design={value}
																	fonts={fonts}
																	modeSelection={modeSelection}
																	disabled={disabled}
																	onChange={next =>
																		setAssignment(
																			preset.id,
																			property,
																			column.key,
																			next
																		)
																	}
																/>
															</td>
														);
													})}
												</tr>
											);
										})}
										<tr>
											<td colSpan={columns.length + 1} className={css.addPropertyRow}>
												<Dropdown
													trigger={['click']}
													disabled={
														disabled ||
														TEXT_PRESET_PROPERTIES.every(
															property => preset.properties[property]
														)
													}
													menu={{
														items: TEXT_PRESET_PROPERTIES.filter(
															property => !preset.properties[property]
														).map(property => ({
															key: property,
															label: t(`textPresets.properties.${property}`),
														})),
														onClick: ({ key }) =>
															setAssignment(
																preset.id,
																key as TextPresetProperty,
																'base',
																defaultLiteral(key as TextPresetProperty, value)
															),
													}}
												>
													<Button
														type="text"
														className={css.addAction}
														icon={<PlusOutlined />}
														disabled={
															disabled ||
															TEXT_PRESET_PROPERTIES.every(
																property => preset.properties[property]
															)
														}
													>
														{t('textPresets.addProperty')}
													</Button>
												</Dropdown>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</Flex>
					),
				}))}
			/>
			{previewPreset ? (
				<TextPresetPreview
					name={previewPreset.name}
					colorScheme={
						value.modeGroups
							.flatMap(group => group.modes.filter(mode => mode.id === modeSelection[group.id]))
							.find(mode => mode.colorScheme)?.colorScheme ?? 'light'
					}
					fonts={fonts}
					onClose={() => setPreviewId(undefined)}
					samples={columns.map(column => ({
						...column,
						referenceWidth:
							column.key === 'base'
								? undefined
								: getPlatformReferenceWidth(mediaQuery, column.key as StoryDefaultPlatform),
						style: getTextPresetPreviewStyle({
							preset: previewPreset,
							platform: column.key as 'base' | StoryDefaultPlatform,
							mediaQuery,
							design: value,
							modeSelection,
						}),
					}))}
				/>
			) : null}
			<Modal
				title={t('textPresets.rename')}
				open={!!renaming}
				onCancel={() => setRenaming(undefined)}
				okText={t('designTokens.save')}
				okButtonProps={{ disabled: disabled || !renaming?.name.trim() }}
				onOk={() => {
					if (!renaming?.name.trim()) return;
					update(renaming.id, preset => ({
						...preset,
						name: renaming.name.trim(),
						description: renaming.description,
					}));
					setRenaming(undefined);
				}}
			>
				<Flex vertical gap="middle">
					<Input
						autoFocus
						aria-label={t('textPresets.name')}
						value={renaming?.name ?? ''}
						disabled={disabled}
						onChange={event => setRenaming(current => current && { ...current, name: event.target.value })}
					/>
					<Input.TextArea
						aria-label={t('textPresets.description')}
						placeholder={t('textPresets.description')}
						value={renaming?.description ?? ''}
						disabled={disabled}
						onChange={event =>
							setRenaming(current => current && { ...current, description: event.target.value })
						}
					/>
				</Flex>
			</Modal>
			{!presets.length ? <Typography.Text type="secondary">{t('textPresets.empty')}</Typography.Text> : null}
		</Flex>
	);
}
